import { useEffect, useState } from 'react';

import { useSearchImplantAccountsQuery } from '@/app/services/inClinic';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';

type BillingAccountSearchDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (accountId: string, accountName: string) => void;
};

export function BillingAccountSearchDialog({
  open,
  onOpenChange,
  onSelect,
}: BillingAccountSearchDialogProps) {
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [pending, setPending] = useState<{ id: string; name: string } | null>(
    null,
  );

  useEffect(() => {
    if (!open) {
      setQuery('');
      setDebounced('');
      setPending(null);
    }
  }, [open]);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);

  const { data: results = [], isFetching } = useSearchImplantAccountsQuery(
    debounced,
    { skip: debounced.length < 2 },
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="md:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {pending
              ? 'Confirm Implant Billing Account'
              : 'Search Billing Account'}
          </DialogTitle>
        </DialogHeader>
        <DialogBody className="flex flex-col gap-3">
          {!pending && (
            <>
              <Input
                autoFocus
                placeholder="Account number or name..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              <div className="max-h-64 overflow-y-auto rounded border">
                {debounced.length < 2 && (
                  <div className="text-muted-foreground p-3 text-xs">
                    Type at least 2 characters to search
                  </div>
                )}
                {debounced.length >= 2 && isFetching && (
                  <div className="text-muted-foreground p-3 text-xs">
                    Searching...
                  </div>
                )}
                {debounced.length >= 2 &&
                  !isFetching &&
                  results.length === 0 && (
                    <div className="text-muted-foreground p-3 text-xs">
                      No accounts found
                    </div>
                  )}
                {results.map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => setPending(r)}
                    className="hover:bg-muted/50 block w-full border-b px-3 py-2 text-left text-sm last:border-b-0"
                  >
                    {r.name}
                  </button>
                ))}
              </div>
            </>
          )}
          {pending && (
            <p className="text-sm">
              Set billing account to{' '}
              <span className="font-semibold">{pending.name}</span>?
            </p>
          )}
        </DialogBody>
        <DialogFooter>
          {pending ? (
            <>
              <Button variant="outline" onClick={() => setPending(null)}>
                Back
              </Button>
              <Button
                onClick={() => {
                  onSelect(pending.id, pending.name);
                  onOpenChange(false);
                }}
              >
                Confirm
              </Button>
            </>
          ) : (
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
